import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  activeTags,
  buildTree,
  facets,
  flattenTree,
  categoryPaths,
  fieldValue,
  filterItems,
  isDeleted,
  isProtected,
  naturalDirection,
  sortItems,
  SORT_KEYS,
  primaryPhoto,
  DEFAULT_SORT,
  UNFILED,
  type AnyEvent,
  type ItemFilter,
  type ItemRecord,
  type ItemSort,
  type SortKey,
  type Manifest,
  type SearchIndex,
  type Visibility,
} from '@intentionalist/shared';
import { storedCatalogView, type CatalogView } from './catalog-view.js';
import { restoreItem } from './mutations.js';
import { formatAmount } from './money.js';
import { FavoriteButton } from './FavoriteButton.js';
import { ItemForm } from './ItemForm.js';
import { PhotoStrip } from './PhotoStrip.js';
import { ItemActions } from './ItemActions.js';
import { TagEditor } from './TagEditor.js';
import { CustomFields } from './CustomFields.js';
import { VerdictPanel } from './VerdictPanel.js';
import type { ReplicaDb } from './db.js';
import { usePhotoSources } from './usePhotoSources.js';

/**
 * The catalog (Epic 3).
 *
 * Reads entirely from the in-memory manifest: no query, no loading state, no
 * network, online or off. That is the whole point of holding a full replica
 * (assumption A7), and it is what NFR-PERF-2 and -3 are measured against.
 *
 * Phase 3 continues from here with the inverted index FR-3.2 wants and the
 * compound-index filters FR-3.3 lists. What is here is the part those build on:
 * the list, an edit path, and soft delete with recovery.
 */
export function Catalog({
  manifest,
  version,
  index,
  db,
  userId,
  onWrite,
  onEditingChange,
}: {
  manifest: Manifest;
  /** For the photo strip, which reads locally captured blobs. */
  db: ReplicaDb;
  userId: string;
  /**
   * Bumped by the parent whenever the manifest changes.
   *
   * The manifest is mutated in place rather than replaced, so its identity is
   * stable and React cannot see an edit. Keying the memo on the number of items
   * would miss a field edit entirely, which is the bug this avoids.
   */
  version: number;
  /** Maintained by the replica, so it never disagrees with the manifest. */
  index: SearchIndex;
  onWrite: (events: AnyEvent[]) => void;
  /**
   * Reports whether an item's page is open, rather than the list.
   *
   * The shell's anchored action and this screen's "Save changes" want the same
   * corner, so the shell has to know which of them belongs there. Told rather
   * than asked, because which item is open is this component's business and
   * lifting it would make the shell responsible for a thing it never reads.
   */
  onEditingChange?: (editing: boolean) => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  /**
   * The category chosen on the open item's page but not yet saved.
   *
   * The form holds what is typed until Save; the panels below it write as they
   * go. That split is deliberate everywhere else, and wrong for one thing: the
   * custom fields a category brings with it. Choosing a category has to ask its
   * questions straight away, so the choice is kept here, where both halves of
   * the page can see it.
   *
   * `undefined` means the picker has not been touched, which is not the same as
   * "unfiled" -- the panel then reads the item's own category.
   */
  const [pendingCategory, setPendingCategory] = useState<string | undefined>(undefined);

  /*
   * In an effect rather than beside each `setEditing`, so that every way out of
   * an item's page reports it -- saving, cancelling, and deleting all clear it,
   * and one of them forgetting would leave the shell with no action in the
   * corner at all.
   */
  useEffect(() => {
    onEditingChange?.(editing !== null);
  }, [editing, onEditingChange]);

  /**
   * An item's page is a place you can come back from.
   *
   * It is state in this component rather than a view, so until now it had no
   * history entry behind it -- and a back gesture on an item's page went to
   * whatever preceded the app, which on an installed phone app means leaving
   * it. Losing the app is a harsh answer to a gesture that everywhere else in
   * it means "up one".
   *
   * So opening an item pushes an entry and closing one consumes it, which keeps
   * the two in step: every way out goes through `closeItem`, and the entry is
   * spent whether the way out was the gesture, a button, a save or a delete. An
   * entry pushed and not consumed is the other bug in this area -- a back press
   * that lands on the page it was already on and appears to do nothing.
   *
   * The URL does not change. Which item is open is this component's business,
   * the way `onEditingChange` already says; `viewFromUrl` reads an unchanged
   * query as the catalog, so the shell agrees with us about where we are
   * without having to be told. The cost is that reloading an item's page shows
   * the list, which is what it did before.
   */
  const openItem = (itemId: string) => {
    setEditing(itemId);
    // Or the next item opened would be asked the last one's questions.
    setPendingCategory(undefined);
    history.pushState({ itemPage: itemId }, '');
  };

  /**
   * Closes the page and spends the entry that opened it.
   *
   * Both, rather than leaving the state to the `popstate` below: `history.back`
   * resolves on its own schedule, and a Save that waits a turn of the event
   * loop to visibly do anything is a Save that gets pressed twice. The handler
   * then clears state that is already clear, which costs a render that React
   * drops anyway.
   */
  const closeItem = () => {
    setEditing(null);
    history.back();
  };

  /*
   * The gesture itself, and the browser's own back button with it.
   *
   * Bound for as long as this screen is mounted rather than only while an item
   * is open, because the entry can outlive the page that pushed it: leaving the
   * catalog for another destination unmounts this component with the entry
   * still on the stack, and coming back to a popped entry should find the list
   * rather than nothing at all.
   */
  useEffect(() => {
    const onPop = () => setEditing(null);
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);

  /**
   * Where the list had been scrolled to, while an item's page is open.
   *
   * `null` means the catalog was not arrived at from an item, which is what
   * keeps the effect below from scrolling a page nobody navigated.
   */
  const listOffset = useRef<number | null>(null);

  /**
   * An item's page opens at the top of itself (#61).
   *
   * The list and the page are the same scrolling document -- opening an item
   * swaps what is rendered and nothing moves the viewport -- so tapping the
   * twentieth row opened its page already scrolled past the name, the photos
   * and the form, onto whichever panel happened to be at that offset. Where the
   * list had been is no description of where a page begins.
   *
   * Going back restores the offset rather than landing at the top, because the
   * place in the list is the one thing the trip out and back should not cost.
   * Scrolling to the top on the way in would otherwise lose it: there is no
   * history entry here to restore, the catalog and the item are one view.
   *
   * A layout effect, so both moves happen before the browser paints. In an
   * effect the frame between the swap and the scroll is painted at the old
   * offset, which is the jump this exists to remove.
   */
  useLayoutEffect(() => {
    if (editing !== null) {
      // Read before the scroll, and after the render that opened the page: the
      // document has changed but the viewport has not yet been moved.
      listOffset.current = window.scrollY;
      window.scrollTo(0, 0);
      return;
    }
    if (listOffset.current !== null) {
      window.scrollTo(0, listOffset.current);
      listOffset.current = null;
    }
  }, [editing]);

  const [filter, setFilter] = useState<ItemFilter>({ visibility: 'active' });
  const [sort, setSort] = useState<ItemSort>(DEFAULT_SORT);
  /**
   * FR-3.1 asks for both a photo-led grid and a compact list.
   *
   * They are browse and manage rather than two renderings of the same thing: the
   * grid is faster to scan, because a photograph identifies an object quicker
   * than its name does, and the list carries the per-item controls.
   *
   * Which one this device opens on is a setting (FR-3.1a), read here as the
   * initial state and not watched afterwards: the toolbar switches freely for
   * the rest of the visit, and nothing it does is written back. Reading it once
   * is enough because Settings is another destination, so this screen is
   * mounted on the way back from changing it -- the same bargain
   * `CurrencyChoice` makes.
   *
   * The list is what it falls back to, for two reasons that both point the same
   * way. It is where the intention control and the delete action live, so
   * opening on the grid would put the things people came to do one tap further
   * away. And a catalog is usually written down before it is photographed, so a
   * new account's grid would be a wall of name tiles while its list shows
   * brand, color and price.
   */
  const [view, setView] = useState<CatalogView>(storedCatalogView);
  const [showFilters, setShowFilters] = useState(false);

  // Every read runs against the in-memory replica: no query, no loading state,
  // the same offline as online. That is assumption A7 being cashed in.
  // Nested categories and locations are included by `filterItems` itself, so
  // that this screen and every other one filtering the same way agree
  // (NFR-FUNC-1). It was special-cased here once, which is exactly how they
  // came to disagree.
  /*
   * Category paths, built once rather than per comparison.
   *
   * Sorting by category needs the taxonomy -- an item carries a category id, and
   * ordering by id is ordering by UUID -- and the sort module has no business
   * knowing about the tree, so the lookup is assembled here and handed in.
   */
  const paths = useMemo(() => categoryPaths(manifest), [manifest, version]);

  const items = useMemo(
    // Sorted after filtering, which is the cheaper order: the filter is the
    // selective step, so the sort has fewer items to compare (FR-3.5 over
    // whatever FR-3.3 left).
    () =>
      sortItems(filterItems(manifest, filter, index), sort, (item) => {
        const id = fieldValue(item, 'categoryId');
        return typeof id === 'string' ? (paths.get(id) ?? null) : null;
      }),
    [manifest, index, version, filter, sort, paths],
  );

  // The options are whatever the manifest contains, because brand, color and
  // condition have no fixed vocabulary (FR-3.3, and the glossary on Condition).
  const options = useMemo(() => facets(manifest), [manifest, version]);

  const categories = useMemo(() => buildTree(manifest.categories), [manifest, version]);
  const locations = useMemo(() => buildTree(manifest.locations), [manifest, version]);
  const tags = useMemo(() => activeTags(manifest), [manifest, version]);

  /**
   * `exactOptionalPropertyTypes` makes `Partial<ItemFilter>` reject an explicit
   * `undefined`, and clearing a filter is exactly that -- so the patch type says
   * so rather than the call sites working around it.
   */
  const set = (patch: ItemFilter) => setFilter((current) => ({ ...current, ...patch }));

  /** '' from a select means "no filter on this", not "match the empty value". */
  const choose = (key: 'brand' | 'color' | 'condition') => (value: string) =>
    set({ [key]: value === '' ? undefined : value });

  const active = Object.entries(filter).filter(
    ([key, value]) => key !== 'visibility' && value !== undefined && value !== '',
  ).length;

  /**
   * One signed-URL request for the whole visible list.
   *
   * Resolved here rather than inside each row: a row that fetched its own would
   * turn a two-hundred-item catalog into two hundred round trips.
   */
  const thumbnails = usePhotoSources(
    db,
    items.flatMap((item) => {
      const photo = primaryPhoto(item);
      return photo === undefined
        ? []
        : [{ photoId: photo.photoId, derivativeKey: photo.metadata?.v.derivativeKey }];
    }),
  );

  const target = editing === null ? undefined : manifest.items.get(editing);

  if (target !== undefined) {
    return (
      <div>
        {/*
          The way out, first on the page -- and again last, in `ItemActions`.
          It used to be a Cancel inside the form, which was fine while the form
          ended the page -- and became a button stranded in the middle of it
          once saving moved to the anchored corner. An item's page is six panels
          deep, so the way back belongs where someone looks for it rather than
          wherever the form happens to stop. Six panels is also too far to ask
          someone to scroll back up, which is why there are two of these: one at
          each end, so the exit is never further away than the end of the page.
        */}
        <div className="actions">
          <button type="button" onClick={closeItem}>
            Back to catalog
          </button>
        </div>

        <ItemForm
          item={target}
          manifest={manifest}
          onWrite={onWrite}
          onSave={(events) => {
            onWrite(events);
            closeItem();
          }}
          onCategoryChange={setPendingCategory}
        />

        {/* Below the form rather than in it, for the reason the tags are: it
            takes effect on the press, not when the form is submitted. */}
        <div className="actions">
          <FavoriteButton item={target} onWrite={onWrite} labelled />
        </div>
        {/* Below the form, and not part of it: both a tag and a photo take
            effect the moment they are added, rather than when the form is
            submitted. */}
        <TagEditor manifest={manifest} item={target} onWrite={onWrite} />
        <CustomFields
          manifest={manifest}
          item={target}
          categoryId={pendingCategory}
          onWrite={onWrite}
        />
        <VerdictPanel item={target} onWrite={onWrite} />
        <PhotoStrip db={db} userId={userId} item={target} onWrite={onWrite} />
        <ItemActions item={target} onWrite={onWrite} onDeleted={closeItem} onBack={closeItem} />
      </div>
    );
  }

  return (
    <div>
      {/*
        Named for a screen reader, not for the screen.
        The destination bar already says Catalog and marks it as the current
        page, so printing it again at the top spent a line on something the
        user had just pressed. The heading stays in the document because
        headings are how NFR-UX-3's screen-reader navigation moves between
        screens, and a destination with no heading at all is one that cannot
        be jumped to.
      */}
      <h2 className="visually-hidden">Catalog</h2>

      <div className="field">
        <label htmlFor="catalog-search">Search</label>
        <input
          id="catalog-search"
          type="search"
          value={filter.text ?? ''}
          onChange={(e) => set({ text: e.target.value === '' ? undefined : e.target.value })}
          placeholder="name, brand, color, notes, tags"
        />
      </div>

      {/*
        Two choices, not five buttons.
        Which items to show and how to show them were one undivided row of
        equal blocks under a single group label that described only the first
        three of them -- so the row read as a clump, and a screen reader was
        told that "Grid" was one of the answers to "which items to show". Each
        is its own group now, labeled for what it actually decides, and drawn
        as one joined control so the eye counts two things rather than five.
      */}
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="Which items to show">
          {(['active', 'deleted', 'archived'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={(filter.visibility ?? 'active') === option}
              onClick={() => set({ visibility: option as Visibility })}
            >
              {option === 'active' ? 'Active' : option === 'deleted' ? 'Deleted' : 'Archived'}
            </button>
          ))}
        </div>

        <div className="segmented" role="group" aria-label="How to show them">
          {(['grid', 'list'] as const).map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={view === option}
              onClick={() => setView(option)}
            >
              {option === 'grid' ? 'Grid' : 'List'}
            </button>
          ))}
        </div>

        <div className="field inline">
          <label htmlFor="catalog-sort">Sort by</label>
          <select
            id="catalog-sort"
            value={sort.key}
            onChange={(e) => {
              const key = e.target.value as SortKey;
              // Reset to the direction that reads most usefully for the new key:
              // newest first for a date, dearest first for money, A to Z for a
              // name. Keeping the previous direction would silently answer a
              // different question from the one just asked.
              setSort({ key, direction: naturalDirection(key) });
            }}
          >
            {SORT_KEYS.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            aria-label={
              sort.direction === 'asc'
                ? 'Sorted ascending, switch to descending'
                : 'Sorted descending, switch to ascending'
            }
            onClick={() =>
              setSort((current) => ({
                ...current,
                direction: current.direction === 'asc' ? 'desc' : 'asc',
              }))
            }
          >
            {/* The arrow is labeled above rather than left to the glyph, which
                a screen reader would read as nothing useful (NFR-UX-4). */}
            {sort.direction === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <button
          type="button"
          aria-expanded={showFilters}
          aria-controls="catalog-filters"
          onClick={() => setShowFilters((open) => !open)}
        >
          {/* The count is on the button because the panel can be closed with
              filters still applied, and an empty-looking list needs explaining. */}
          Filters{active > 0 ? ` (${active})` : ''}
        </button>

        {active > 0 && (
          <button
            type="button"
            onClick={() => setFilter({ visibility: filter.visibility ?? 'active' })}
          >
            Clear filters
          </button>
        )}
      </div>

      {showFilters && (
        <div id="catalog-filters" className="grid-fields">
          {/*
            In the panel rather than the toolbar. It is one more thing to narrow
            by, and the row of buttons above has been crowded before -- whereas
            the panel is where someone already is when they want to narrow.

            A checkbox, not a select: there are two states worth offering and
            "not a favorite" is not one of them.
          */}
          <div className="field">
            <label>
              <input
                type="checkbox"
                checked={filter.favorite === true}
                onChange={(e) => set({ favorite: e.target.checked ? true : undefined })}
              />
              Favorites only
            </label>
          </div>

          <div className="field">
            <label htmlFor="filter-intention">Intention</label>
            <select
              id="filter-intention"
              value={filter.intention ?? ''}
              onChange={(e) =>
                set({
                  intention:
                    e.target.value === ''
                      ? undefined
                      : (e.target.value as NonNullable<ItemFilter['intention']>),
                })
              }
            >
              <option value="">Any</option>
              {/* Unreviewed is the absence of a verdict, so it cannot be one of
                  the four values (glossary). */}
              <option value="unreviewed">Unreviewed</option>
              <option value="keep">Keep</option>
              <option value="uncertain">Uncertain</option>
              <option value="release">Release</option>
              <option value="invest">Invest</option>
            </select>
          </div>

          {(
            [
              ['brand', 'Brand', options.brands],
              ['color', 'Color', options.colors],
              ['condition', 'Condition', options.conditions],
            ] as const
          ).map(([key, label, values]) =>
            values.length === 0 ? null : (
              <div className="field" key={key}>
                <label htmlFor={`filter-${key}`}>{label}</label>
                <select
                  id={`filter-${key}`}
                  value={filter[key] ?? ''}
                  onChange={(e) => choose(key)(e.target.value)}
                >
                  <option value="">Any</option>
                  {values.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </select>
              </div>
            ),
          )}

          {(
            [
              ['categoryId', 'Category', flattenTree(categories), 'Not in any category'],
              ['locationId', 'Location', flattenTree(locations), null],
            ] as const
          ).map(([key, label, nodes, unfiled]) =>
            nodes.length === 0 ? null : (
              <div className="field" key={key}>
                <label htmlFor={`filter-${key}`}>{label}</label>
                <select
                  id={`filter-${key}`}
                  value={filter[key] ?? ''}
                  onChange={(e) =>
                    set({ [key]: e.target.value === '' ? undefined : e.target.value })
                  }
                >
                  <option value="">Any</option>
                  {/* The one answer the tree cannot give. Above the categories
                      rather than below them, because a list three levels deep
                      can be long enough to hide an option at its foot -- and
                      this one is about the whole list rather than a part of it. */}
                  {unfiled !== null && <option value={UNFILED}>{unfiled}</option>}
                  {nodes.map((node) => (
                    <option key={node.id} value={node.id}>
                      {/* Indented, so the shape of the tree survives a flat list. */}
                      {'\u00a0\u00a0'.repeat(node.depth - 1)}
                      {node.name}
                    </option>
                  ))}
                </select>
              </div>
            ),
          )}

          {tags.length > 0 && (
            <div className="field">
              <label htmlFor="filter-tag">Tag</label>
              <select
                id="filter-tag"
                value={filter.tagId ?? ''}
                onChange={(e) => set({ tagId: e.target.value === '' ? undefined : e.target.value })}
              >
                <option value="">Any</option>
                {tags.map((tag) => (
                  <option key={tag.tagId} value={tag.tagId}>
                    {tag.displayName?.v}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="field">
            <label htmlFor="filter-from">Acquired from</label>
            <input
              id="filter-from"
              type="date"
              value={filter.acquiredFrom ?? ''}
              onChange={(e) => set({ acquiredFrom: e.target.value || undefined })}
            />
          </div>

          <div className="field">
            <label htmlFor="filter-to">Acquired to</label>
            <input
              id="filter-to"
              type="date"
              value={filter.acquiredTo ?? ''}
              onChange={(e) => set({ acquiredTo: e.target.value || undefined })}
            />
          </div>
        </div>
      )}

      <p className="muted" aria-live="polite">
        {items.length} item{items.length === 1 ? '' : 's'}
        {filter.text === undefined || filter.text.trim() === ''
          ? ''
          : ` matching “${filter.text.trim()}”`}
      </p>

      {items.length === 0 ? (
        <p className="muted">
          {active > 0
            ? 'Nothing matches those filters.'
            : (filter.visibility ?? 'active') === 'active'
              ? 'Nothing catalogued yet.'
              : filter.visibility === 'deleted'
                ? 'Nothing deleted. Deleted items stay here for 30 days.'
                : 'Nothing archived. An item is archived once its release is recorded.'}
        </p>
      ) : (
        <ul
          className={view === 'grid' ? 'catalog-grid' : 'catalog'}
          aria-label={view === 'grid' ? 'Catalog grid' : 'Catalog list'}
        >
          {items.map((item) =>
            view === 'grid' ? (
              <Tile
                key={item.itemId}
                item={item}
                thumbnails={thumbnails}
                onOpen={() => openItem(item.itemId)}
                onWrite={onWrite}
              />
            ) : (
              <Row
                key={item.itemId}
                item={item}
                thumbnails={thumbnails}
                onEdit={() => openItem(item.itemId)}
                onWrite={onWrite}
              />
            ),
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * One item in the photo-led grid (FR-3.1).
 *
 * The whole tile is the control, rather than a card with a button in it: on a
 * phone the photograph is the obvious thing to tap, and a separate hit target
 * inside it would be both smaller and surprising. Being a `button` rather than a
 * `div` with a click handler is what makes it reachable by keyboard and
 * announced as actionable (NFR-UX-2, NFR-UX-3).
 *
 * An item without a photograph gets its name at tile size rather than an empty
 * frame or a placeholder graphic. A catalog is often photographed later than
 * it is written down, and a grid of identical grey boxes would be useless
 * exactly when the catalog is newest.
 */
function Tile({
  item,
  thumbnails,
  onOpen,
  onWrite,
}: {
  item: ItemRecord;
  thumbnails: Record<string, string>;
  onOpen: () => void;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const name = String(fieldValue(item, 'name') ?? '(unnamed)');
  const photo = primaryPhoto(item);
  const source = photo === undefined ? undefined : thumbnails[photo.photoId];
  const price = formatAmount(fieldValue(item, 'purchasePrice'));

  return (
    <li className="tile-cell">
      {/*
        Over the tile rather than inside it: the whole tile is the button that
        opens the item, and a button cannot contain another one. Laid over the
        corner, which is where a star on a photograph belongs anyway.
      */}
      <FavoriteButton item={item} onWrite={onWrite} />

      <button type="button" className="tile" onClick={onOpen} aria-label={`Open ${name}`}>
        {source === undefined ? (
          <span className="tile-name" aria-hidden="true">
            {name}
          </span>
        ) : (
          <img
            className="tile-photo"
            src={source}
            alt=""
            /* Decorative: the tile's accessible name already carries the item's
               name, so describing the photograph as well would announce it
               twice. */
            loading="lazy"
            decoding="async"
          />
        )}

        <span className="tile-caption">
          <strong>{name}</strong>
          <span className="muted">
            {/* Unreviewed is the absence of an intention, not a value (glossary). */}
            {item.currentIntention !== null && (
              <span className="badge">{item.currentIntention}</span>
            )}
            {isProtected(item) && <span className="badge">protected</span>}
            {price !== '' && ` ${price}`}
          </span>
        </span>
      </button>
    </li>
  );
}

function Row({
  item,
  thumbnails,
  onEdit,
  onWrite,
}: {
  item: ItemRecord;
  thumbnails: Record<string, string>;
  onEdit: () => void;
  onWrite: (events: AnyEvent[]) => void;
}) {
  const name = String(fieldValue(item, 'name') ?? '(unnamed)');
  const quantity = fieldValue(item, 'quantity') ?? 1;
  const price = formatAmount(fieldValue(item, 'purchasePrice'));
  const descriptors = (['brand', 'color', 'size', 'material'] as const)
    .map((field) => fieldValue(item, field))
    .filter((value): value is string => typeof value === 'string' && value !== '');

  const deleted = isDeleted(item);
  const guarded = isProtected(item);

  // FR-2.4 exists so that one photo represents the item. Until something showed
  // it, the primary flag was written and never read.
  const photo = primaryPhoto(item);
  const source = photo === undefined ? undefined : thumbnails[photo.photoId];

  return (
    <li>
      <div className="catalog-row">
        {/*
          Outside the row button, not inside it: a button within a button is
          neither valid nor operable, which is the same reason the verdict
          control and Delete were never in there either.
        */}
        <FavoriteButton item={item} onWrite={onWrite} />

        {/*
          The row itself opens the item, instead of an Edit button beside it.
          A button labelled Edit next to the thing it edits is a control earning
          its place by convention rather than by use: the row is the obvious
          target, and it is a far larger one on a phone (NFR-UX-1).

          A real `button` rather than a click handler on the row, because the
          row has to be reachable by Tab and announced as something that can be
          pressed. It wraps only the photograph and the description -- the
          verdict control and Delete stay outside it, since a button inside a
          button is neither valid nor operable.

          Spans rather than divs inside it for the same reason: a button may
          only contain phrasing content, and the layout is the stylesheet's job
          regardless.
        */}
        <button type="button" className="row-main row-open" onClick={onEdit}>
          {source !== undefined && (
            <img className="row-thumb" src={source} alt={`Photo of ${name}`} />
          )}
          <span>
            <strong>{name}</strong>
            {quantity !== 1 && <span className="muted"> ×{quantity}</span>}
            {/* Unreviewed is the absence of an intention, not a value (glossary). */}
            {item.currentIntention !== null && (
              <span className="badge">{item.currentIntention}</span>
            )}
            {/* Marked in words, not by color alone (NFR-UX-4), and with a title
                because the badge is terse. */}
            {guarded && (
              <span className="badge" title="Kept out of bulk changes">
                protected
              </span>
            )}
            <span className="muted row-descriptors">
              {descriptors.join(' · ')}
              {descriptors.length > 0 && price !== '' ? ' · ' : ''}
              {price === '' ? '' : price}
            </span>
          </span>
        </button>

        {/*
          Restore, and nothing else.
          A verdict control and a Delete button sat here, on every row, for two
          actions that are taken rarely and are both on the item's own page --
          which the row now opens. They cost a control's worth of width per item
          on the screen the catalog is read on, and the photo-led grid never
          carried them at all.

          Restore stays because it is the point of the view it appears in: the
          Deleted filter exists to undo a deletion, and the row is the only
          place to do it.
        */}
        {deleted && (
          <div className="actions">
            <button type="button" onClick={() => onWrite(restoreItem(item.itemId))}>
              Restore
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
